// `/admin/reports` (`spec/24-croqui-alinhamento.md` §5, WEB-79).
//
// Período, seller escolhido e o drawer "Exportar CSV" vivem na query string (`spec/17`
// §3): recarregar, compartilhar o link e o botão de voltar continuam valendo. O drawer não
// é um `RouteDrawer` porque não há rota `$id` aqui (ver "Questões abertas" da task).

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { isSellerId } from '../api/queries/admin-reports.ts';
import type { IsoRange } from '../api/queries/reports.ts';
import { rangeForPreset } from '../components/kit/date-range-filter.tsx';
import {
  AdminReportsScreen,
  DEFAULT_PRESET,
} from '../features/admin-reports/admin-reports-screen.tsx';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** `?from=banana` cai em `undefined` (e no preset default) em vez de derrubar a rota. */
const searchSchema = z.object({
  from: z.string().regex(ISO_DATE).optional().catch(undefined),
  to: z.string().regex(ISO_DATE).optional().catch(undefined),
  seller: z.string().refine(isSellerId).optional().catch(undefined),
  novo: z.boolean().optional().catch(undefined),
});

function AdminReports() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const fallback = rangeForPreset(DEFAULT_PRESET);
  const range: IsoRange = {
    from: search.from ?? fallback.from,
    to: search.to ?? fallback.to,
  };

  /** `undefined` em vez de `false`: a URL limpa é a do drawer fechado. */
  const go = (next: IsoRange, seller: string | undefined, novo: boolean): void => {
    void navigate({
      to: '/admin/reports',
      search: {
        ...(next.from === fallback.from ? {} : { from: next.from }),
        ...(next.to === fallback.to ? {} : { to: next.to }),
        ...(seller === undefined ? {} : { seller }),
        ...(novo ? { novo: true } : {}),
      },
      replace: true,
    });
  };

  const novo = search.novo === true;

  return (
    <AdminReportsScreen
      range={range}
      onRangeChange={(next) => {
        go(next, search.seller, novo);
      }}
      sellerId={search.seller}
      onSellerChange={(seller) => {
        go(range, seller, novo);
      }}
      newExportOpen={novo}
      onNewExportOpenChange={(open) => {
        go(range, search.seller, open);
      }}
    />
  );
}

export const Route = createFileRoute('/admin/reports/')({
  validateSearch: searchSchema,
  component: AdminReports,
});
